/**
 * In-memory live price cache for the miniapp session.
 * Survives route changes; TTL is refreshed on each price tick (sliding window).
 */

import type {
  ExchangeCode,
  TokenAvailability,
} from "@/lib/market-data/market-data.types";
import type { PriceUpdate } from "@/lib/live-pricing/live-pricing.types";

/** Default ~2 minutes — stale entries are ignored for hydration. */
export const LIVE_PRICE_CACHE_TTL_MS = 120_000;

export interface CachedLivePriceBundle {
  price: PriceUpdate;
  availability: TokenAvailability;
  cachedAt: number;
}

export interface CachedDynamicLivePrice extends CachedLivePriceBundle {
  exchange: ExchangeCode;
}

const pinned = new Map<string, CachedLivePriceBundle>();
const dynamic = new Map<string, CachedDynamicLivePrice>();

export function pinnedLivePriceCacheKey(
  exchange: ExchangeCode,
  symbolNorm: string,
  futuresOnly: boolean
): string {
  return `${exchange}:${symbolNorm}:${futuresOnly ? "f" : "fs"}`;
}

export function dynamicLivePriceCacheKey(
  symbolNorm: string,
  futuresOnly: boolean
): string {
  return `${symbolNorm}:${futuresOnly ? "f" : "fs"}`;
}

function isStale(entry: CachedLivePriceBundle): boolean {
  return Date.now() - entry.cachedAt > LIVE_PRICE_CACHE_TTL_MS;
}

export function readPinnedLivePriceCache(
  exchange: ExchangeCode,
  symbolNorm: string,
  futuresOnly: boolean
): CachedLivePriceBundle | null {
  const key = pinnedLivePriceCacheKey(exchange, symbolNorm, futuresOnly);
  const e = pinned.get(key);
  if (!e || isStale(e)) {
    pinned.delete(key);
    return null;
  }
  return e;
}

export function readDynamicLivePriceCache(
  symbolNorm: string,
  futuresOnly: boolean
): CachedDynamicLivePrice | null {
  const key = dynamicLivePriceCacheKey(symbolNorm, futuresOnly);
  const e = dynamic.get(key);
  if (!e || isStale(e)) {
    dynamic.delete(key);
    return null;
  }
  return e;
}

export function recordLivePriceCache(
  exchange: ExchangeCode,
  symbolNorm: string,
  futuresOnly: boolean,
  availability: TokenAvailability,
  price: PriceUpdate
): void {
  const now = Date.now();
  const bundle: CachedLivePriceBundle = {
    price,
    availability,
    cachedAt: now,
  };
  pinned.set(pinnedLivePriceCacheKey(exchange, symbolNorm, futuresOnly), bundle);
  dynamic.set(dynamicLivePriceCacheKey(symbolNorm, futuresOnly), {
    ...bundle,
    exchange,
  });
}
